import React, { useState, useEffect } from 'react';
import { Layers, ChevronDown, ChevronRight, Package, Scissors, AlertTriangle, Printer, Archive } from 'lucide-react';
import { toast } from 'sonner';
import fetchWithAuth from '../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

const BarVisual = ({ bar, stockLength }) => {
  const barLen = bar.bar_length_mm || stockLength;
  const scale = (mm) => `${(mm / barLen) * 100}%`;
  const colors = [
    'bg-cyan-500', 'bg-emerald-500', 'bg-amber-500', 'bg-blue-500',
    'bg-violet-500', 'bg-rose-500', 'bg-teal-500', 'bg-orange-500',
  ];

  let offset = 0;
  const segments = [];
  const endDrop = bar.end_drop_mm / 2;

  // Leading end drop (only on new stock, not remnants)
  if (endDrop > 0) {
    segments.push(
      <div key="end-l" className="absolute h-full bg-slate-600/40"
        style={{ left: scale(offset), width: scale(endDrop) }}
        title={`End drop: ${endDrop}mm`} />
    );
    offset += endDrop;
  }

  bar.pieces.forEach((piece, i) => {
    // Kerf before piece (not first)
    if (i > 0 && bar.total_kerf_mm > 0) {
      const kerfPerCut = bar.total_kerf_mm / Math.max(1, bar.piece_count - 1);
      segments.push(
        <div key={`kerf-${i}`} className="absolute h-full bg-red-500/60"
          style={{ left: scale(offset), width: scale(kerfPerCut) }}
          title={`Kerf: ${kerfPerCut.toFixed(1)}mm`} />
      );
      offset += kerfPerCut;
    }
    // Piece
    const colorIdx = i % colors.length;
    segments.push(
      <div key={`piece-${i}`} className={`absolute h-full ${colors[colorIdx]} opacity-80 border-r border-slate-900/50`}
        style={{ left: scale(offset), width: scale(piece.cut_length_mm) }}
        title={`${piece.part_number}: ${piece.cut_length_mm}mm`}>
        {piece.cut_length_mm / barLen > 0.08 && (
          <span className="absolute inset-0 flex items-center justify-center text-[8px] text-white font-mono truncate px-0.5">
            {piece.cut_length_mm}
          </span>
        )}
      </div>
    );
    offset += piece.cut_length_mm;
  });

  // Trailing end drop
  if (endDrop > 0) {
    segments.push(
      <div key="end-r" className="absolute h-full bg-slate-600/40 right-0"
        style={{ width: scale(endDrop) }}
        title={`End drop: ${endDrop}mm`} />
    );
  }

  return (
    <div className={`relative h-5 rounded-sm overflow-hidden border ${bar.is_from_remnant ? 'bg-amber-950/30 border-amber-500/50' : 'bg-slate-800 border-slate-700/50'}`} data-testid={`nesting-bar-${bar.bar_index}`}>
      {segments}
      {/* Remnant indicator */}
      {bar.remnant_mm > 0 && (
        <div className={`absolute h-full right-0 ${bar.is_remnant_waste ? 'bg-red-900/30' : 'bg-emerald-900/20'} border-l border-dashed ${bar.is_remnant_waste ? 'border-red-600/40' : 'border-emerald-600/40'}`}
          style={{ width: scale(bar.remnant_mm) }}
          title={`Remnant: ${bar.remnant_mm}mm ${bar.is_remnant_waste ? '(waste)' : '(usable)'}`} />
      )}
    </div>
  );
};

const ProfileCard = ({ profile }) => {
  const [expanded, setExpanded] = useState(false);
  const n = profile.nesting;
  const c = profile.cost;
  const utilColor = n.utilization_pct >= 80 ? 'text-emerald-400' :
    n.utilization_pct >= 50 ? 'text-amber-400' : 'text-red-400';
  const utilBg = n.utilization_pct >= 80 ? 'bg-emerald-500/10' :
    n.utilization_pct >= 50 ? 'bg-amber-500/10' : 'bg-red-500/10';

  return (
    <div className="border border-slate-700/40 rounded-md bg-slate-900/30" data-testid={`nesting-profile-${profile.profile_code}`}>
      {/* Header */}
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-800/40 transition-colors text-left"
        data-testid={`nesting-profile-toggle-${profile.profile_code}`}>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
        <Package className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-slate-200 truncate">{profile.profile_name}</div>
          <div className="text-[10px] text-slate-500 font-mono">{profile.profile_code} | {profile.material_grade} | {profile.stock_length_mm}mm stock</div>
        </div>
        <div className="flex items-center gap-3 text-[11px] shrink-0">
          <div className={`px-2 py-0.5 rounded ${utilBg}`}>
            <span className={`font-mono font-semibold ${utilColor}`}>{n.utilization_pct}%</span>
            <span className="text-slate-500 ml-1">util</span>
          </div>
          <div className="text-right">
            <div className="font-mono text-slate-300">{n.bars_needed} bar{n.bars_needed !== 1 ? 's' : ''}</div>
            <div className="text-[9px] text-slate-500">
              {n.remnant_bars_used > 0 && <span className="text-amber-400">{n.remnant_bars_used} remnant</span>}
              {n.remnant_bars_used > 0 && n.new_bars_needed > 0 && ' + '}
              {n.new_bars_needed > 0 && <span>{n.new_bars_needed} new</span>}
              {!n.remnant_bars_used && !n.new_bars_needed && `${profile.demand.total_pieces} pcs`}
            </div>
          </div>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-slate-700/30">
          {/* Stats row */}
          <div className="flex gap-4 pt-2 text-[10px]">
            <div>
              <span className="text-slate-500">Total Cut:</span>
              <span className="text-slate-300 font-mono ml-1">{(n.total_cut_mm / 1000).toFixed(2)}m</span>
            </div>
            <div>
              <span className="text-slate-500">Stock Used:</span>
              <span className="text-slate-300 font-mono ml-1">{(n.total_stock_mm / 1000).toFixed(1)}m</span>
            </div>
            <div>
              <span className="text-slate-500">Waste:</span>
              <span className="text-red-400 font-mono ml-1">{n.waste_pct}%</span>
            </div>
            {c.bars_saved > 0 && (
              <div className="ml-auto">
                <span className="text-emerald-400 font-mono">{c.bars_saved} bar{c.bars_saved !== 1 ? 's' : ''} saved</span>
                <span className="text-emerald-500/50 ml-1">(-₹{c.cost_saving.toLocaleString()})</span>
              </div>
            )}
          </div>

          {/* Cost comparison */}
          <div className="flex gap-4 text-[10px] p-2 bg-slate-800/30 rounded">
            <div>
              <span className="text-slate-500">BOM Allocated:</span>
              <span className="text-slate-400 font-mono ml-1">₹{c.allocated_cost.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-slate-500">Procurement:</span>
              <span className="text-cyan-300 font-mono ml-1">₹{c.optimized_procurement_cost.toLocaleString()}</span>
              <span className="text-slate-600 ml-1">({c.optimized_bars} new bar{c.optimized_bars !== 1 ? 's' : ''} x ₹{profile.cost_per_stock_unit.toLocaleString()})</span>
            </div>
            {n.remnant_bars_used > 0 && (
              <div>
                <span className="text-amber-400 font-mono">+{n.remnant_bars_used} remnant</span>
                <span className="text-slate-600 ml-1">(₹0)</span>
              </div>
            )}
          </div>

          {/* Demand parts */}
          <div className="text-[10px]">
            <div className="text-slate-500 uppercase tracking-wider mb-1 font-semibold">Demand</div>
            <div className="space-y-0.5">
              {profile.demand.unique_parts.map((part, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-400">
                  <Scissors className="w-3 h-3 text-slate-600" />
                  <span className="font-mono text-slate-300">{part.part_number}</span>
                  <span className="truncate">{part.part_description}</span>
                  <span className="ml-auto text-slate-500">{part.cut_length_mm}mm x {part.total_pieces}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bar visualizations */}
          <div className="text-[10px]">
            <div className="text-slate-500 uppercase tracking-wider mb-1.5 font-semibold">Cut Plan</div>
            <div className="space-y-2">
              {n.bars.map((bar) => (
                <div key={bar.bar_index} className="space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">
                      {bar.is_from_remnant ? (
                        <span className="text-amber-400">Remnant Bar {bar.bar_index} <span className="text-amber-500/60">({bar.bar_length_mm}mm)</span></span>
                      ) : (
                        <>Bar {bar.bar_index} <span className="text-slate-600">({bar.bar_length_mm || profile.stock_length_mm}mm new)</span></>
                      )}
                    </span>
                    <span className="text-slate-500">
                      {bar.piece_count} pc | {bar.consumed_mm}mm used | {bar.remnant_mm}mm
                      <span className={bar.is_remnant_waste ? 'text-red-400' : 'text-emerald-400'}>
                        {bar.is_remnant_waste ? ' waste' : ' remnant'}
                      </span>
                      {' | '}<span className={bar.utilization_pct >= 70 ? 'text-emerald-400' : 'text-amber-400'}>{bar.utilization_pct}%</span>
                    </span>
                  </div>
                  <BarVisual bar={bar} stockLength={profile.stock_length_mm} />
                  <div className="flex gap-2 flex-wrap pl-1">
                    {bar.pieces.map((p, pi) => (
                      <span key={pi} className="text-[9px] text-slate-500">
                        <span className="text-slate-400 font-mono">{p.part_number}</span>:{p.cut_length_mm}mm
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex gap-3 text-[9px] text-slate-600 pt-1 border-t border-slate-700/20 flex-wrap">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-cyan-500/80" /> Cut piece</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500/60" /> Kerf</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-600/40" /> End drop</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-900/40 border border-dashed border-emerald-600/40" /> Usable remnant</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-900/40 border border-dashed border-red-600/40" /> Waste</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-950/30 border border-amber-500/50" /> From remnant stock</span>
          </div>
        </div>
      )}
    </div>
  );
};

export const NestingSummaryPanel = ({ bomId, revisionId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bomId || !revisionId) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetchWithAuth(
          `${API}/api/v1/bom/${bomId}/revisions/${revisionId}/nesting-summary`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [bomId, revisionId]);

  if (loading) return <div className="px-5 py-3 text-[11px] text-slate-500" data-testid="nesting-loading">Loading nesting analysis...</div>;
  if (error) return <div className="px-5 py-3 text-[11px] text-red-400" data-testid="nesting-error">Nesting error: {error}</div>;
  if (!data || data.summary.profiles_with_linear_stock === 0) {
    return (
      <div className="px-5 py-3 text-[11px] text-slate-500 flex items-center gap-2" data-testid="nesting-empty">
        <Layers className="w-3.5 h-3.5" />
        No linear stock materials in this BOM. Nesting applies to pipes, bars, cables, and structural sections.
      </div>
    );
  }

  const s = data.summary;

  return (
    <div className="px-5 py-3 space-y-3 border-t border-slate-700/30 animate-in fade-in duration-200" style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }} data-testid="nesting-summary-panel">
      {/* Summary stats */}
      <div className="flex items-center gap-4 text-[11px]">
        <div className="flex items-center gap-1.5">
          <Layers className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-slate-400 font-medium">Linear Stock Nesting</span>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <div>
            <span className="text-slate-500">Profiles:</span>
            <span className="text-slate-300 font-mono ml-1">{s.profiles_with_linear_stock}</span>
          </div>
          <div>
            <span className="text-slate-500">Bars:</span>
            <span className="text-cyan-300 font-mono ml-1">{s.total_bars_needed}</span>
            {s.remnant_bars_used > 0 && (
              <span className="text-amber-400 font-mono ml-1">({s.remnant_bars_used} remnant)</span>
            )}
          </div>
          <div>
            <span className="text-slate-500">Utilization:</span>
            <span className={`font-mono ml-1 ${s.overall_utilization_pct >= 70 ? 'text-emerald-400' : s.overall_utilization_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
              {s.overall_utilization_pct}%
            </span>
          </div>
          <div>
            <span className="text-slate-500">Procurement:</span>
            <span className="text-slate-300 font-mono ml-1">₹{s.optimized_procurement_cost.toLocaleString()}</span>
          </div>
          {s.bars_saved > 0 && (
            <div className="ml-auto flex items-center gap-1 text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
              <span className="font-mono">{s.bars_saved} bar{s.bars_saved > 1 ? 's' : ''} saved</span>
              <span className="text-emerald-500/60">(-₹{s.total_saving.toLocaleString()})</span>
            </div>
          )}
          {s.overall_utilization_pct < 50 && (
            <div className="ml-auto flex items-center gap-1 text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">
              <AlertTriangle className="w-3 h-3" />
              <span>Low utilization</span>
            </div>
          )}
        </div>
      </div>

      {/* Profile cards */}
      <div className="space-y-2">
        {data.profiles.map((profile) => (
          <ProfileCard key={profile.profile_id} profile={profile} />
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1 border-t border-slate-700/20">
        <button onClick={async () => {
          try {
            const res = await fetchWithAuth(
              `${API}/api/v1/bom/${bomId}/revisions/${revisionId}/cut-list`,
              { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            if (!res.ok) throw new Error('Failed');
            const cl = await res.json();
            // Open print view
            const w = window.open('', '_blank', 'width=900,height=700');
            const genDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            w.document.write(`<!DOCTYPE html><html><head><title>Cut List - ${cl.bom_number}</title>
              <style>body{font-family:monospace;font-size:12px;padding:24px;color:#222}
              .doc-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:4px}
              .doc-header h1{font-size:16px;font-weight:700;margin:0}
              .doc-header .doc-meta{text-align:right;font-size:11px;color:#666;line-height:1.7}
              .doc-header .doc-meta strong{font-weight:700}
              .header-sep{border-top:1px solid #ccc;margin-top:10px;margin-bottom:14px}
              h2{font-size:13px;margin-top:16px;border-bottom:1px solid #ccc;padding-bottom:4px}
              table{width:100%;border-collapse:collapse;margin-top:8px}th,td{border:1px solid #ddd;padding:4px 8px;text-align:left}
              th{background:#f5f5f5;font-weight:bold}.bar-hdr{background:#e8f0f8;font-weight:bold}
              .meta{color:#666;font-size:11px}
              .print-btn{background:#222;color:#fff;border:none;padding:6px 16px;cursor:pointer;font-family:monospace;font-size:11px;font-weight:700;margin-bottom:12px}
              .print-btn:hover{background:#444}
              .remnant-row{background:#fff8e1}.remnant-badge{background:#f59e0b;color:#fff;padding:1px 6px;border-radius:3px;font-size:10px;margin-left:4px}
              .new-badge{background:#3b82f6;color:#fff;padding:1px 6px;border-radius:3px;font-size:10px;margin-left:4px}
              .doc-footer{margin-top:24px;border-top:1px solid #ccc;padding-top:10px;display:flex;justify-content:space-between;font-size:10px;color:#666}
              @media print{.print-btn{display:none}}</style></head><body>
              <button class="print-btn" onclick="window.print()">Print Cut List</button>
              <div class="doc-header">
                <div>
                  <h1>CUT LIST &mdash; ${cl.bom_number} ${cl.product_name}</h1>
                  <div class="meta">Rev ${cl.revision_number} | Build Qty: ${cl.build_qty} | Generated: ${new Date(cl.generated_at).toLocaleString()}</div>
                  <div class="meta">Total Bars: ${cl.summary.total_bars_needed} (${cl.summary.new_bars_needed || cl.summary.total_bars_needed} new${cl.summary.remnant_bars_used ? ', ' + cl.summary.remnant_bars_used + ' remnant' : ''}) | Utilization: ${cl.summary.overall_utilization_pct}% | New Stock Cost: ₹${cl.summary.optimized_procurement_cost?.toLocaleString()}</div>
                </div>
                <div class="doc-meta">
                  <div>Revision: <strong>${cl.revision_number}</strong></div>
                  <div>Date: ${genDate}</div>
                  <div>${cl.summary.total_bars_needed} bars &middot; ${cl.summary.overall_utilization_pct}% utilization</div>
                </div>
              </div>
              <div class="header-sep"></div>`);
            cl.cut_lists.forEach(p => {
              const remInfo = p.remnant_bars_used > 0 ? ` | ${p.remnant_bars_used} from remnant stock` : '';
              w.document.write(`<h2>${p.profile_name} (${p.profile_code})</h2>
                <div class="meta">${p.material_grade} | Stock: ${p.stock_length_mm}mm | Kerf: ${p.kerf_loss_mm}mm | ${p.new_bars_needed || p.bars_needed} new bar(s)${remInfo}</div>
                <table><thead><tr><th>Bar #</th><th>Source</th><th>Bar Length</th><th>Cuts</th><th>Cut Total</th><th>Kerf</th><th>Leftover</th><th>Usable</th><th>Util%</th></tr></thead><tbody>`);
              p.bars.forEach(bar => {
                const cutsStr = bar.cuts.map(c => c.part_number + ': ' + c.cut_length_mm + 'mm').join(', ');
                const rowClass = bar.is_from_remnant ? ' class="remnant-row"' : '';
                const srcBadge = bar.is_from_remnant
                  ? `<span class="remnant-badge">REMNANT</span>`
                  : `<span class="new-badge">NEW</span>`;
                const barLenStr = `${bar.stock_length_mm}mm`;
                w.document.write(`<tr${rowClass}><td>${bar.bar_number}</td><td>${srcBadge}</td><td>${barLenStr}</td><td>${cutsStr}</td><td>${bar.total_cut_mm}mm</td><td>${bar.kerf_mm}mm</td><td>${bar.remnant_mm}mm</td><td>${bar.is_remnant_usable ? 'Yes' : 'No'}</td><td>${bar.utilization_pct}%</td></tr>`);
              });
              w.document.write('</tbody></table>');
            });
            w.document.write(`<div class="doc-footer"><span>Generated from HADEN ATLAS PLM</span><span>Printed: ${genDate}</span></div>`);
            w.document.write('</body></html>');
            w.document.close();
            toast.success('Cut list opened in new tab');
          } catch { toast.error('Failed to generate cut list'); }
        }}
          className="flex items-center gap-1 px-2 py-1 text-[10px] bg-slate-700/40 hover:bg-slate-600/40 rounded text-slate-300 hover:text-white transition-colors"
          data-testid="nesting-export-cut-list">
          <Printer className="w-3 h-3" /> Export Cut List
        </button>
        <button onClick={async () => {
          try {
            const res = await fetchWithAuth(
              `${API}/api/v1/bom/${bomId}/revisions/${revisionId}/log-remnants`,
              { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` } }
            );
            if (!res.ok) throw new Error('Failed');
            const r = await res.json();
            const parts = [];
            if (r.consumed > 0) parts.push(`consumed ${r.consumed} remnant${r.consumed !== 1 ? 's' : ''}`);
            if (r.logged > 0) parts.push(`logged ${r.logged} new remnant${r.logged !== 1 ? 's' : ''}`);
            toast.success(parts.join(', ') || 'No remnants to log');
          } catch { toast.error('Failed to log remnants'); }
        }}
          className="flex items-center gap-1 px-2 py-1 text-[10px] bg-emerald-700/20 hover:bg-emerald-700/30 rounded text-emerald-400 hover:text-emerald-300 transition-colors"
          data-testid="nesting-log-remnants">
          <Archive className="w-3 h-3" /> Log Remnants
        </button>
      </div>
    </div>
  );
};
